/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useMemo } from 'react';

import { useLocation } from 'js/lib/useRouter';
import { isAuthenticatedUser } from 'js/lib/user';

import { Grid, Hidden } from '@coursera/cds-core';
import type { Recommender } from '@coursera/event-pulse-types';

import { getShouldShowCourseraPlusMerchandisingPlacements } from 'bundles/coursera-plus-common/utils/subscriptionsForwardDiscoveryExperiment';
import useIsOnLocaleSubfolderRoute from 'bundles/internationalization-lib/hooks/useIsOnLocaleSubfolderRoute';
import ActiveFilterItems from 'bundles/search-common/components/ActiveFilterItems';
import EndOfResults from 'bundles/search-common/components/EndOfResults';
import SearchPageBodySections from 'bundles/search-common/components/SearchPageBodySections';
import SearchResultsHeader from 'bundles/search-common/components/SearchResultsHeader';
import SearchResultsPagination from 'bundles/search-common/components/SearchResultsPagination';
import SortByIndex from 'bundles/search-common/components/SortByIndex';
import SuggestedSearches from 'bundles/search-common/components/SuggestedSearches';
import { NUMBER_OF_RESULTS_PER_PAGE, SEARCH_MAIN_INDEX_ID } from 'bundles/search-common/constants';
import { useOwnsCourseraPlus } from 'bundles/search-common/providers/LearnerProvider';
import { useSearch } from 'bundles/search-common/providers/SearchContext';
import { getCombinedHits, getUpsellCardPosition } from 'bundles/search-common/utils/SearchUtils';
import { inferQueryFromParams } from 'bundles/search-common/utils/eventingUtils';
import { isInFilterTestVariant } from 'bundles/search-common/utils/experimentUtils';
import useIsConsumerSerp from 'bundles/search-common/utils/useIsConsumerSerp';
import useIsEqp from 'bundles/search-common/utils/useIsEqp';
import {
  isSearchSuggestionHit,
  mapSuggestionHitToHighlightItem,
  shouldShowSuggestedSearches,
} from 'bundles/search-common/utils/utils';
import { CoachChatLayer } from 'bundles/search-v2/components/experiments/coachInSearch/CoachChatLayer';
import { SearchCoachDisclosure } from 'bundles/search-v2/components/experiments/coachInSearch/SearchCoachDisclosure';
import { getCoachSearchQueries } from 'bundles/search-v2/components/experiments/coachInSearch/coachSearchQueries';
import { showCoachInSearch } from 'bundles/search-v2/utils/experiments';
import useIsMobile from 'bundles/unified-common/hooks/useIsMobile';
import { useCoachState } from 'bundles/unified-common/providers/CoachStateProvider';

import _t from 'i18n!nls/search';

export const styles = {
  header: css`
    display: flex;
    justify-content: space-between;
    margin-bottom: var(--cds-spacing-150);

    /* breakpoints.md */
    @media (max-width: 1023px) {
      min-height: var(--cds-spacing-400);
    }

    @media (min-width: 600px) {
      padding-left: var(--cds-spacing-100);
      padding-right: var(--cds-spacing-100);
    }
  `,
  headerMobile: css`
    margin-bottom: 0;
  `,
  mobileSortByLayoutShim: css`
    margin-top: var(--cds-spacing-50);
  `,
  fullHeight: css`
    height: 100%;
  `,
  desktopSearchControlsWrapper: css`
    display: flex;
    gap: var(--cds-spacing-100);
  `,
  filterTestResultItems: css`
    flex-shrink: 1;
  `,
  filterTestSortBy: css`
    flex-shrink: 0;

    & > div {
      margin-left: auto;
    }
  `,
};

export type EventingData = {
  searchIndex: string;
  searchRecommender?: Recommender;
  searchIndexPosition: number;
  page?: string | undefined;
  searchQuery?: string | undefined;
  filtersApplied?: Record<string, string[]> | undefined;
};

type Props = {
  isMobile: boolean;
  optimisticActiveFilterCount: number;
  isSEOEntityPageWithQuery: boolean;
  showNoResultsRecommendations: boolean;
  eventingData: EventingData;
  paginationType: 'infinite' | 'control';
  searchQuery?: string | undefined;
  page: number;
};

const SearchPageBody = ({
  isMobile,
  optimisticActiveFilterCount,
  isSEOEntityPageWithQuery,
  showNoResultsRecommendations,
  eventingData,
  paginationType,
  searchQuery,
  page,
}: Props) => {
  const isConsumerSearchPage = useIsConsumerSerp();
  const isOnLocaleSubfolderRoute = useIsOnLocaleSubfolderRoute();
  const { getSearchResults, setPage, removeFilters, clearFilters, setSortBy, loadNextPage, isLoading } = useSearch();
  const searchData = getSearchResults(SEARCH_MAIN_INDEX_ID);
  const {
    query,
    pagination,
    totalPages,
    facetFilters,
    hitsDisplayedCount = 0,
    hitsTotalCount = 0,
    inferredCurrentPageNumber = 0,
    sortBy,
    elements,
  } = searchData || {};
  const totalHits = pagination?.totalElements;
  const suggestedSearches = getSearchResults('suggestions');
  const showSortByFilter = !isSEOEntityPageWithQuery;
  const activeFilters = useMemo(() => [...new Set(facetFilters?.flat())], [facetFilters]);

  const isOnLastPage =
    !isLoading && (inferredCurrentPageNumber >= (totalPages ?? 0) || hitsDisplayedCount === hitsTotalCount);
  const loadNextSearchPage = () => {
    if (!isOnLastPage) {
      loadNextPage();
    }
  };
  const shouldShowInfiniteStylePagination = paginationType === 'infinite';
  const isOnEQPSubfolderRoute = isOnLocaleSubfolderRoute && isSEOEntityPageWithQuery;

  const inFilterTestVariantAndOnSearch = isInFilterTestVariant() && isConsumerSearchPage;
  const suggestedSearchHits = useMemo(
    () => suggestedSearches?.elements.filter(isSearchSuggestionHit),
    [suggestedSearches]
  );

  const isEligibleForCoachInSearch: boolean =
    !isSEOEntityPageWithQuery && isAuthenticatedUser() && Boolean(getCoachSearchQueries()[query || '']);

  const { setCoachOpen } = useCoachState();

  useEffect(() => {
    setCoachOpen(false);
    // query in dep list because we want to close coach if the query changes
  }, [setCoachOpen, query]);

  const isMobileView = useIsMobile();
  const ownsCourseraPlus = useOwnsCourseraPlus();
  const showMerchandisingPlacements = !ownsCourseraPlus && getShouldShowCourseraPlusMerchandisingPlacements();
  const upsellCardIndex = page === 1 ? getUpsellCardPosition(isMobileView, showMerchandisingPlacements) : -1;
  const isSeoEntityPagePath = useIsEqp();
  const showUpsellCard = isSeoEntityPagePath && upsellCardIndex !== -1;
  const addOverFlowCard = !showUpsellCard && showMerchandisingPlacements;

  const urlParams = useLocation().search;
  const searchParams = useMemo(() => {
    const urlParamsObject = new URLSearchParams(urlParams);
    urlParamsObject.delete('page');
    const params: Record<string, string> = {};
    urlParamsObject.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  }, [urlParams]);

  const combinedHits = isLoading
    ? []
    : getCombinedHits({
        hits: elements,
        page,
        totalHits,
        showUpsellCard,
        upsellCardIndex,
        searchParams,
        addOverFlowCard,
      });
  return (
    <CoachChatLayer searchHits={searchData?.elements ?? []} isEligibleForCoachInSearch={isEligibleForCoachInSearch}>
      <>
        <Grid
          container
          justifyContent="space-between"
          css={!isMobile ? [styles.header, isMobile && styles.headerMobile] : null}
        >
          <Grid
            item
            {...(inFilterTestVariantAndOnSearch && { xs: 6, sm: 8 })}
            css={[styles.mobileSortByLayoutShim, inFilterTestVariantAndOnSearch && styles.filterTestResultItems]}
            id="search-results-header-wrapper"
          >
            <div aria-live="polite" aria-atomic="true">
              <SearchResultsHeader
                searchQuery={inferQueryFromParams(searchQuery || '')}
                numberOfHits={totalHits || undefined}
                shouldHide={isMobile && !inFilterTestVariantAndOnSearch}
                activeFilterCount={optimisticActiveFilterCount}
                isLoading={isLoading && hitsDisplayedCount === 0}
                lessenHeaderFocus={true}
                hideHits={true}
              />
            </div>
          </Grid>
          <Grid
            item
            {...(inFilterTestVariantAndOnSearch && { xs: 6, sm: 4 })}
            css={[styles.desktopSearchControlsWrapper, inFilterTestVariantAndOnSearch && styles.filterTestSortBy]}
          >
            {/* desktop sortby */}
            {(!isMobile || inFilterTestVariantAndOnSearch) && showSortByFilter && !showNoResultsRecommendations && (
              <SortByIndex
                setSortBy={(sortByValue) => setSortBy(SEARCH_MAIN_INDEX_ID, sortByValue)}
                sortByValue={sortBy}
                hideLabelOnMobile={inFilterTestVariantAndOnSearch}
              />
            )}
          </Grid>
        </Grid>
        {!isMobile && !inFilterTestVariantAndOnSearch && activeFilters && (
          <Hidden smDown>
            <ActiveFilterItems
              items={activeFilters}
              removeFilter={(filter: string) => removeFilters(SEARCH_MAIN_INDEX_ID, [filter])}
              clearFilters={() => clearFilters(SEARCH_MAIN_INDEX_ID)}
              eventingData={eventingData}
              isLoading={isLoading}
            />
          </Hidden>
        )}
        <div role="region" aria-label={_t('Search Results')} id="searchResults">
          {paginationType === 'control' && (
            <SearchPageBodySections
              hits={combinedHits || []}
              resultsPerPage={NUMBER_OF_RESULTS_PER_PAGE}
              forceOpenInNewTab
              eventingData={eventingData}
              isLoading={isLoading}
              isSearchPage={isConsumerSearchPage}
              isSeoEntityPagePath={isSeoEntityPagePath}
              page={page}
            />
          )}

          {!isOnEQPSubfolderRoute &&
            !shouldShowInfiniteStylePagination &&
            shouldShowSuggestedSearches(query, suggestedSearches?.elements) && (
              <div>
                <SuggestedSearches
                  query={query}
                  hits={mapSuggestionHitToHighlightItem(suggestedSearchHits, query)}
                  eventingData={eventingData}
                />
              </div>
            )}

          {shouldShowInfiniteStylePagination && (
            <>
              {/* first search cards section  */}
              <SearchPageBodySections
                hits={searchData?.elements.slice(0, NUMBER_OF_RESULTS_PER_PAGE) || []}
                resultsPerPage={NUMBER_OF_RESULTS_PER_PAGE}
                forceOpenInNewTab
                eventingData={eventingData}
                isLoading={isLoading && !hitsDisplayedCount}
                isSearchPage={isConsumerSearchPage}
                onScrolledToBottom={loadNextSearchPage}
                totalHitsDisplayed={hitsDisplayedCount}
                isFirstSection={true}
              />
              {isOnLastPage && hitsDisplayedCount < NUMBER_OF_RESULTS_PER_PAGE && (
                <EndOfResults hitsDisplayedCount={hitsDisplayedCount} hitsTotalCount={hitsTotalCount} />
              )}

              {/* Coach in Search experiment */}
              {showCoachInSearch() && isEligibleForCoachInSearch && (
                <div css={{ marginTop: '24px', marginBottom: '50px' }}>
                  <SearchCoachDisclosure query={query || ''} setCoachOpen={setCoachOpen} />
                </div>
              )}

              {/* suggestions  */}
              {!(showCoachInSearch() && isEligibleForCoachInSearch) &&
                !isOnEQPSubfolderRoute &&
                (suggestedSearches?.elements.length ?? 0) > 0 &&
                shouldShowSuggestedSearches(query, suggestedSearches?.elements) && (
                  <SuggestedSearches
                    query={query}
                    hits={mapSuggestionHitToHighlightItem(suggestedSearchHits, query)}
                    eventingData={eventingData}
                    css={{ marginTop: '31px', marginBottom: '97px' }}
                  />
                )}

              {/* second search cards section */}
              {hitsDisplayedCount >= NUMBER_OF_RESULTS_PER_PAGE && (
                <SearchPageBodySections
                  hits={searchData?.elements.slice(NUMBER_OF_RESULTS_PER_PAGE) || []}
                  resultsPerPage={NUMBER_OF_RESULTS_PER_PAGE}
                  forceOpenInNewTab
                  eventingData={eventingData}
                  isLoading={isLoading}
                  isSearchPage={isConsumerSearchPage}
                  onScrolledToBottom={loadNextSearchPage}
                  totalHitsDisplayed={hitsDisplayedCount}
                  cardIndexOffset={NUMBER_OF_RESULTS_PER_PAGE}
                  isFirstSection={false}
                />
              )}

              {isOnLastPage && hitsDisplayedCount > NUMBER_OF_RESULTS_PER_PAGE && (
                <EndOfResults hitsDisplayedCount={hitsDisplayedCount} hitsTotalCount={hitsTotalCount} />
              )}
            </>
          )}

          {!shouldShowInfiniteStylePagination && (
            <SearchResultsPagination
              shouldRenderLinks={isSEOEntityPageWithQuery}
              activeFilters={activeFilters}
              paginationData={{ ...pagination, totalPages, page }}
              currentQuery={query}
              eventingData={eventingData}
              setPage={(pageNum: number) => setPage(SEARCH_MAIN_INDEX_ID, pageNum)}
            />
          )}
        </div>
      </>
    </CoachChatLayer>
  );
};

export default SearchPageBody;
